import axios, { AxiosError, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { performSignOut } from '@/utils/auth/perform-sign-out';
import { env } from '@/utils/shared/env.ts';
import { AuthState } from '@/components/authentication-guard/types';
import type { CloudAuthState } from '@itential/cloud-auth-client';

let cloudAuthStateRef: CloudAuthState | null = null;

/**
 * Sets the cloud auth state reference for use in token refresh operations
 *
 * @param {CloudAuthState} authState - The cloud auth state object
 */
export function setCloudAuthStateRef(authState: CloudAuthState): void {
    cloudAuthStateRef = authState;
}

/**
 * Retrieves the current JWT bearer token from localStorage.
 *
 * @returns {string | null} The JWT access token if available, or null.
 */
export function getBearerToken(): string | null {
    const realm = localStorage.getItem('realm');
    if (!realm) console.error('Invalid realm');
    const tokenKey = `${realm}user:${env.VITE_KEYCLOAK_URL}/auth:hub`;

    const localData = localStorage.getItem(tokenKey);
    if (!localData || localData === null) return null;

    try {
        const parsed = JSON.parse(localData);
        return parsed.access_token ? `Bearer ${parsed.access_token}` : null;
    } catch (e) {
        console.error('Failed to fetch jwt access token from localStorage:', e);
        return null;
    }
}

/**
 * Attempts to refresh the JWT access token using a refresh token stored in an httpOnly cookie.
 * Brings the user to the login page if the token cannot be refreshed
 *
 * @returns {Promise<void>} whether the token was refreshed or not the function will return void.
 */
export async function refreshJwt(): Promise<void> {
    try {
        if (!cloudAuthStateRef) {
            console.error('Cloud auth state not initialized');
            performSignOut();
            return;
        }

        if (
            cloudAuthStateRef.state === AuthState.SIGNED_IN &&
            'refreshHubToken' in cloudAuthStateRef &&
            typeof cloudAuthStateRef.refreshHubToken === 'function'
        ) {
            return await cloudAuthStateRef.refreshHubToken();
        }
    } catch (error) {
        console.error('JWT refresh failed', error);
        performSignOut();
    }
}

/**
 * Injects a bearer token into the request configuration headers.
 *
 * @param {InternalAxiosRequestConfig} config - The Axios request configuration to modify.
 * @returns {Promise<InternalAxiosRequestConfig>} A promise that resolves to the updated Axios request configuration.
 */
export async function injectBearerToken(
    config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
    const bearerToken: string | null = getBearerToken();
    config.headers.set('Authorization', bearerToken);

    return config;
}

/**
 * Handles 401 & 403 authentication errors by refreshing the JWT and retrying the request.
 * Should only be called for 401 & 403 errors (caller's responsibility to filter).
 * Makes 1 attempt to refresh the JWT, and retry the original request with a new token.
 *
 * @param {AxiosError} error - The Axios error object with 401 or 403 status.
 * @returns {Promise<AxiosResponse | never>} A promise that resolves with the retried request if the token is refreshed,
 * or rejects with the original error if the request has already been retried.
 */
export async function refreshRetryOnTokenExpiry(error: AxiosError): Promise<AxiosResponse | never> {
    const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (config._retry) {
        return Promise.reject(error);
    }

    config._retry = true;

    try {
        await refreshJwt();
        await injectBearerToken(config);
        return axios(config);
    } catch (refreshError) {
        return Promise.reject(refreshError);
    }
}
